import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Segment } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import RadarChart from '@/pages/dashboard/components/Chart/components/RadarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import WorldMapEchart from '@/pages/dashboard/components/MapEchart';
import CardBox from '@/pages/dashboard/components/CardBox';
import CommonTable from '@/pages/dashboard/components/CommonTable';
import MetricsCard from '@/pages/dashboard/components/MetricsCard';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { DownloadButton } from '@/pages/dashboard/components/Plugin/DownloadButton';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import nameMap from '@/pages/dashboard/components/MapEchart/_config/nameMap.json';
import TipContext from '@home/components/TipContext';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { flattenZoneAgg, getNetworkLatencyState } from './NetworkLatencyService';
import { getPerfOverviewState } from './PerformanceOverviewService';
import { CountryRegionOptionsContext } from './PerformanceFilter';
import {
  buildFeatureNameIndex,
  getCountryIso2,
  getCountryMapCenter,
  getCountryMapZoom,
  isGeoCountryOption,
  isRegionCountryName,
  loadCountryMapJson,
  toMapRegionName,
} from './utils/countryMap';
import './index.scss';

type Platform = 'android' | 'ios';
type Period = 'day' | 'week';

const NETWORK_COLORS = ['#5B6CFF', '#F0A33A', '#B6BDCC', '#8C9AAF', '#F1749C'];
const MAP_COLORS = ['#26C477', '#5FD11D', '#E4D900', '#F3B800', '#F26322'];
const MAP_GEO_NAME_ALIASES: Record<string, string> = {
  USA: 'United States',
  US: 'United States',
  UK: 'United Kingdom',
  China: 'China (Mainland)',
  中国: 'China (Mainland)',
  中国大陆: 'China (Mainland)',
  美国: 'United States',
  英国: 'United Kingdom',
  印度尼西亚: 'Indonesia',
  印尼: 'Indonesia',
  Bangladesh: 'Bengal',
  孟加拉: 'Bengal',
  孟加拉国: 'Bengal',
  Libya: 'Libyan Arab Jamahiriya (the)',
  Sudan: 'Sudan (the)',
  Iran: 'Iran (the Islamic Republic of)',
  Korea: 'South Korea',
  Palestine: 'Palestinian Territory (the Occupied)',
  'Central African Republic': 'Central African Republic (the)',
  'Central African Rep.': 'Central African Republic (the)',
  中非: 'Central African Republic (the)',
  中非共和国: 'Central African Republic (the)',
  "Cote d'Ivoire": "Côte d'Ivoire",
  科特迪瓦: "Côte d'Ivoire",
  'Dem. Rep. Congo': 'Democratic Republic of the Congo',
  刚果民主共和国: 'Democratic Republic of the Congo',
  '刚果（金）': 'Democratic Republic of the Congo',
  'Dem. Rep. Korea': "Korea (the Democratic People's Republic of)",
  'S. Sudan': 'South Sudan',
  'Eq. Guinea': 'Equatorial Guinea',
  'Cayman Is.': 'Cayman Islands',
  'Antigua and Barb.': 'Antigua and Barbuda',
  'Bosnia and Herz.': 'Bosnia and Herzegovina',
  'Br. Indian Ocean Ter.': 'British Indian Ocean Territory (the)',
  'Fr. Polynesia': 'French Polynesia',
  'Falkland Is.': 'Falkland Islands',
  'N. Mariana Is.': 'Northern Mariana Islands',
  'St. Vin. and Gren.': 'St. Vincent and The Grenadines',
  'Turks and Caicos Is.': 'Turks and Caicos',
  'U.S. Virgin Is.': 'Virgin Islands (U.S.)',
  'Dominican Rep.': 'Dominican Republic',
  'Czech Rep.': 'Czech Republic',
  'Solomon Is.': 'Solomon Islands',
  'Lao PDR': 'Laos',
  'Russian Federation': 'Russia',
  刚果共和国: 'Congo',
  'Republic of the Congo': 'Congo',
  'Congo (Brazzaville)': 'Congo',
  北马其顿: 'Macedonia',
  'North Macedonia': 'Macedonia',
  'The Republic of North Macedonia': 'Macedonia',
  'N. Macedonia': 'Macedonia',
  Czechia: 'Czech Republic',
};
const NAME_MAP_CN_TO_EN: Record<string, string> = Object.keys(nameMap).reduce(
  (result, enName) => {
    result[nameMap[enName]] = enName;
    return result;
  },
  {} as Record<string, string>,
);

const resolveMapGeoName = (item: any): string => {
  const candidates = [item?.CountryNameEn, item?.CountryName].filter(Boolean);
  for (const name of candidates) {
    if (MAP_GEO_NAME_ALIASES[name]) return MAP_GEO_NAME_ALIASES[name];
    if (nameMap[name]) return name;
    if (NAME_MAP_CN_TO_EN[name]) {
      const enName = NAME_MAP_CN_TO_EN[name];
      return MAP_GEO_NAME_ALIASES[enName] || enName;
    }
  }
  return '';
};

const getMapSeriesName = (item: any): string => {
  const geoName = resolveMapGeoName(item);
  if (!geoName) return '';
  return getLocale() === 'zh-CN' ? nameMap[geoName] || geoName : geoName;
};

const isMappedCountryName = (name: string): boolean =>
  Boolean(name && (nameMap[name] || NAME_MAP_CN_TO_EN[name]));

const buildMapPieces = (ranges: number[], colors: string[]) =>
  ranges.map((min, index) =>
    index === ranges.length - 1
      ? { min, color: colors[index] }
      : { min, max: ranges[index + 1], color: colors[index] },
  );

/** 根据延迟值落入的分段，返回与 mapColors / mapLegend.colorIndex 一致的下标 */
const getMapPieceIndex = (value: number, ranges: number[] = []): number => {
  if (!ranges.length || !Number.isFinite(value)) return -1;
  for (let index = 0; index < ranges.length - 1; index += 1) {
    if (value >= ranges[index] && value < ranges[index + 1]) {
      return index;
    }
  }
  return ranges.length - 1;
};

const DIST_METRIC_KEYS = ['p0', 'p30', 'p60', 'p120', 'p180'];

const niceNumber = (value: number, round: boolean): number => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const exp = Math.floor(Math.log10(value));
  const mag = 10 ** exp;
  const fraction = value / mag;
  let niceFraction = 10;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
  } else if (fraction <= 1) {
    niceFraction = 1;
  } else if (fraction <= 2) {
    niceFraction = 2;
  } else if (fraction <= 5) {
    niceFraction = 5;
  }
  return niceFraction * mag;
};

const buildMapRangesFromMax = (maxValue: number, pieceCount = 5): number[] => {
  const niceMax = niceNumber(maxValue, false) || 200;
  const step = niceNumber(niceMax / Math.max(pieceCount - 1, 1), true) || 50;
  return Array.from({ length: pieceCount }, (_, index) => index * step);
};

const buildMapLegendFromRanges = (
  ranges: number[] = [],
  unit = 'ms',
) => {
  if (ranges.length < 2) return [];
  const lastIndex = ranges.length - 1;
  const withUnit = (text: string) =>
    unit ? `${text}${unit}` : text;
  // 大到小：高档在上
  return ranges.map((_, index) => {
    const colorIndex = lastIndex - index;
    if (colorIndex === lastIndex) {
      return {
        label: withUnit(`${ranges[colorIndex]}+`),
        colorIndex,
      };
    }
    if (colorIndex === 0) {
      return {
        label: withUnit(`${ranges[0]}-${ranges[1]}`),
        colorIndex,
      };
    }
    return {
      label: withUnit(`${ranges[colorIndex]}-${ranges[colorIndex + 1]}`),
      colorIndex,
    };
  });
};

const applyDistRangeLabels = (columns: any[], ranges: number[] = []) => {
  if (ranges.length < 2) return columns;
  const labels = ranges.slice(0, -1).map((min: number, index: number) => {
    const max = ranges[index + 1];
    return index === 0
      ? `0-${max}(ms) 占比(%)`
      : `${min}-${max}(ms) 占比(%)`;
  });
  labels.push(`>${ranges[ranges.length - 1]}(ms) 占比(%)`);
  const keepDistKeys = new Set(DIST_METRIC_KEYS.slice(0, labels.length));
  const remapColumn = (column: any): any | null => {
    if (column?.children) {
      const children = column.children
        .map(remapColumn)
        .filter(Boolean);
      if (!children.length) {
        return null;
      }
      return { ...column, children };
    }
    if (
      DIST_METRIC_KEYS.includes(column?.code) &&
      !keepDistKeys.has(column.code)
    ) {
      return null;
    }
    const distIndex = DIST_METRIC_KEYS.indexOf(column?.code);
    if (distIndex < 0 || !labels[distIndex]) {
      return column;
    }
    return { ...column, nameCn: labels[distIndex], nameEn: labels[distIndex] };
  };
  return columns.map(remapColumn).filter(Boolean);
};

const formatTrendXAxisLabel = (value: string): string => {
  const text = String(value || '');
  if (text.length >= 19 && text[10] === ' ') {
    return `${text.slice(0, 10)}\n${text.slice(11, 19)}`;
  }
  return text;
};

const NETWORK_OPTIONS = [
  { text: '全部', value: 'all' },
  { text: 'WIFI', value: 'wifi' },
  { text: '5G', value: '5g' },
  { text: '4G', value: '4g' },
  { text: '3G', value: '3g' },
  { text: '2G', value: '2g' },
];

const formatMetric = (value: number, formatRule = 'twoDecimal') =>
  dataFormat({ value, formatRule });

const NETWORK_TYPE_NAMES: Record<number, string> = {
  0: 'MATCHES',
  1: 'WIFI',
  2: '2G',
  3: '3G',
  4: '4G',
  5: '5G',
  6: '5G',
};

const isEmptyMetricValue = (value: any) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

const toMetricNumber = (value: any): number | null => {
  if (isEmptyMetricValue(value)) return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const resolveChartMetricValue = (
  value: any,
  nullAsZero?: boolean,
): number | null => {
  const numberValue = toMetricNumber(value);
  return nullAsZero ? numberValue ?? 0 : numberValue;
};

const ratio = (value: any): number | null => {
  const numberValue = toMetricNumber(value);
  return numberValue === null ? null : numberValue / 100;
};

const sumRatios = (...values: any[]): number | null => {
  const ratioValues = values.map((value) =>
    typeof value === 'string' && value.trim() === '' ? 0 : ratio(value),
  );
  if (ratioValues.some((value) => value === null)) return null;
  return (ratioValues as number[]).reduce(
    (total, value) => total + value,
    0,
  );
};

const toTableRow = (item: any) => ({
  games: toMetricNumber(item?.MergeCount),
  share: ratio(item?.CountRatio),
  latency: toMetricNumber(item?.Value),
  p0: ratio(item?.LatencyDist1Ratio),
  p30: ratio(item?.LatencyDist2Ratio),
  p60: ratio(item?.LatencyDist3Ratio),
  p120: ratio(item?.LatencyDist4Ratio),
  p180: sumRatios(
    item?.LatencyDist5Ratio,
    item?.LatencyDist6Ratio,
    item?.LatencyDist7Ratio,
    item?.LatencyDist8Ratio,
    item?.LatencyDist9Ratio,
    item?.LatencyDist10Ratio,
  ),
});
const SectionTitle = ({ title, subtitle, operation, info }: any) => (
  <div className="dbd-performance-section-title">
    <div className="dbd-performance-section-heading">
      <div className="dbd-performance-section-heading-main">
        <span>{title}</span>
        {info ? (
          <TipContext
            className="dbd-performance-section-info"
            tipText={info}
            placement="top"
          />
        ) : null}
      </div>
      {subtitle ? (
        <span className="dbd-performance-section-subtitle">{subtitle}</span>
      ) : null}
    </div>
    {operation ? <div>{operation}</div> : null}
  </div>
);

const getConfigTitleInfo = (
  config: any,
  fallbackCn: string,
  fallbackEn: string,
) =>
  getLocale() === 'en-US'
    ? config?.titleInfoEn || fallbackEn
    : config?.titleInfoCn || fallbackCn;

const DATA_TIME_TIMEZONE_OFFSET = 8;

const normalizeDateStr = (value: any): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  const str = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(str) ? str.slice(0, 10) : '';
};

const getTodayDateStr = (
  timezoneOffset = DATA_TIME_TIMEZONE_OFFSET,
): string => {
  const shifted = new Date(Date.now() + timezoneOffset * 3600000);
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate(),
  )}`;
};

const getDataTimeRange = (data: any, filterVal?: any) => {
  const times =
    (Array.isArray(data?.netDataTimes) && data.netDataTimes.length
      ? data.netDataTimes
      : Array.isArray(data?.netDelayTrend?.xData) && data.netDelayTrend.xData.length
        ? data.netDelayTrend.xData
        : data?.netDelayTime) || [];
  if (!times.length) {
    return '';
  }
  const start = String(times[0]);
  let end = String(times[times.length - 1]);
  const rangeTime = filterVal?.rangeTime || filterVal?.rangeDaily || [];
  const endDate =
    normalizeDateStr(rangeTime?.[1] || rangeTime?.[0]) ||
    normalizeDateStr(end);
  // 历史日期按整天查询，展示到 23:59:59；当天仍用接口最后小时点
  if (endDate && endDate !== getTodayDateStr()) {
    end = `${endDate} 23:59:59`;
  }
  return start === end ? start : `${start} - ${end}`;
};

const FilterSummary = ({ data, countryText }: any) => {
  const { filterVal } = useContext(FilterContext) || ({} as any);
  const rangeText = getDataTimeRange(data, filterVal);
  if (!rangeText && !countryText) {
    return null;
  }
  return (
    <div className="dbd-performance-filter-summary">
      {rangeText ? <span>数据时间：{rangeText}</span> : null}
      {countryText ? <span>国家/地区：{countryText}</span> : null}
    </div>
  );
};

const API_CARD_KEY_MAP: Record<string, string> = {
  fpsMean: 'avg_fps',
  fpsSwingHour: 'fps_jitter',
  jankFrameHour: 'fps_stutter',
  pdSmallJankM10: 'jank',
  pdBigJankM10: 'big_jank',
  pss: 'pss_peak',
  powerMean: 'avg_power',
  matchCount: 'matches',
};

const parseRatioPercent = (value: any): number | null => {
  if (isEmptyMetricValue(value)) return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue * 100 : null;
};

export const PerformanceOverview = ({
  platform = 'android',
  updateTime,
  config = {},
  data: propsData,
  dataKey = 'perfOverview',
}: {
  platform: Platform;
  updateTime?: string;
  config?: any;
  data?: any;
  dataKey?: string;
}) => {
  const [period, setPeriod] = useState<Period>('day');
  const { data, loading, isError } = getPerfOverviewState(propsData, dataKey);
  const radarMetrics = config?.radarMetrics || [];
  const componentMetrics = config?.componentMetrics || [];
  const matchMetric = config?.matchMetric || {};
  const showRadar = config?.showRadar === true;
  const block = period === 'week' ? data?.lastWeek : data?.last24h;
  const cards = (Array.isArray(block?.cards) ? block.cards : []).filter(
    (card: any) =>
      card?.key !== 'matchCount' &&
      !(
        platform === 'ios' &&
        (card?.key === 'powerMean' || card?.key === 'avg_power')
      ),
  );
  const displayUpdateTime = data?.endTime || updateTime || '';
  const metricConfigByKey = useMemo(() => {
    const result: Record<string, any> = {};
    componentMetrics.forEach((item: any) => {
      if (item?.metricKey) {
        result[item.metricKey] = item;
      }
    });
    if (matchMetric?.metricKey) {
      result[matchMetric.metricKey] = matchMetric;
    }
    return result;
  }, [componentMetrics, matchMetric]);
  const isGoodRate = (rate: number, betterDirection?: 'up' | 'down') =>
    betterDirection === 'down' ? rate <= 0 : rate >= 0;
  const getRateClassName = (
    percent: number | null,
    betterDirection?: 'up' | 'down',
  ) => {
    if (percent === null) {
      return '';
    }
    return isGoodRate(percent, betterDirection) ? 'is-good' : 'is-bad';
  };
  const metricCardStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    margin: 0,
  };
  const buildRateItem = (
    titleCn: string,
    titleEn: string,
    ratioValue: any,
    metricValue: any,
    betterDirection?: 'up' | 'down',
    formatRule = 'percent2Decimal',
  ) => {
    const percent = parseRatioPercent(ratioValue);
    const isMissing = percent === null;
    return {
      titleCn,
      titleEn,
      value: isMissing ? '' : ratioValue,
      thisValue: metricValue,
      lastValue: metricValue,
      thisTime: '',
      lastTime: '',
      formatRule,
      hideBubbleContent: true,
      className: getRateClassName(percent, betterDirection),
    };
  };
  const getCardMetricConfig = (card: any, index: number) => {
    const configKey = API_CARD_KEY_MAP[card?.key] || card?.key;
    if (metricConfigByKey[configKey]) {
      return metricConfigByKey[configKey];
    }
    if (card?.key === 'matchCount') {
      return matchMetric;
    }
    return componentMetrics[index] || {};
  };
  const getCardDodData = (card: any, metricConfig: any) => {
    if (card?.key === 'matchCount') {
      return [];
    }
    const value = card?.value;
    const formatRule = metricConfig?.rateFormatRule || 'percent2Decimal';
    const betterDirection = metricConfig?.betterDirection;
    if (period === 'week') {
      return [
        buildRateItem(
          config?.wowRateLabelCn || '周环比',
          config?.wowRateLabelEn || 'WoW',
          card?.wow,
          value,
          betterDirection,
          formatRule,
        ),
      ];
    }
    return [
      buildRateItem(
        config?.dayRateLabelCn || '日环比',
        config?.dayRateLabelEn || 'DoD',
        card?.dod,
        value,
        betterDirection,
        formatRule,
      ),
      buildRateItem(
        config?.weekRateLabelCn || '上周同期',
        config?.weekRateLabelEn || 'Last Week',
        card?.sdlw,
        value,
        betterDirection,
        formatRule,
      ),
    ];
  };

  return (
    <Card className="dbd-performance-card dbd-performance-overview-card">
      <Card.Body>
        <SectionTitle
          title={config?.titleCn || '性能总览'}
          subtitle={
            !loading && !isError && cards.length && displayUpdateTime
              ? `${config?.updateTimeLabelCn || '数据更新处理时间：'}${displayUpdateTime}`
              : ''
          }
          operation={
            <Segment
              value={period}
              options={
                config?.periodOptions || [
                  { text: '近24小时', value: 'day' },
                  { text: '近一周', value: 'week' },
                ]
              }
              onChange={(value: Period) => setPeriod(value)}
            />
          }
        />
        <Abnormal
          isLoading={!!loading}
          isError={isError}
          isNoData={!loading && !isError && !cards.length}
          component={
            <div
              className={`dbd-performance-overview-content${
                showRadar ? '' : ' dbd-performance-overview-content--no-radar'
              }`}
            >
              {showRadar ? (
                <div className="dbd-performance-radar-wrap">
                  <RadarChart
                    height={config?.radarHeight || 270}
                    isShowLegend={false}
                    indicator={radarMetrics.map((metric: any) => ({
                      name: metric?.nameCn,
                      max: metric?.max || 100,
                    }))}
                    series={[
                      {
                        type: 'radar',
                        symbol: 'circle',
                        symbolSize: 7,
                        data: [
                          {
                            value: radarMetrics.map((metric: any) => {
                              const card = cards.find(
                                (item: any) =>
                                  API_CARD_KEY_MAP[item?.key] ===
                                    metric?.metricKey ||
                                  item?.key === metric?.metricKey,
                              );
                              return toMetricNumber(card?.value);
                            }),
                            name: platform,
                          },
                        ],
                        lineStyle: { color: '#5B6CFF', width: 2 },
                        itemStyle: { color: '#5B6CFF' },
                        areaStyle: { color: 'rgba(91, 108, 255, .28)' },
                        label: {
                          show: true,
                          formatter: ({ value, dataIndex }: any) =>
                            formatMetric(
                              value,
                              radarMetrics?.[dataIndex]?.formatRule ||
                                'twoDecimal',
                            ),
                        },
                      },
                    ]}
                    setOtherConfigFunc={(option: any) => ({
                      ...option,
                      tooltip: { show: false },
                      radar: {
                        ...option.radar,
                        radius: '68%',
                        splitNumber: 5,
                        tooltip: { show: false },
                      },
                    })}
                  />
                </div>
              ) : null}
              <div className="dbd-performance-metric-grid">
                {cards.map((card: any, index: number) => {
                  const isMatchCard = card?.key === 'matchCount';
                  const metricConfig = getCardMetricConfig(card, index);
                  const value = card?.value;
                  return (
                    <div
                      className="dbd-performance-metric-cell"
                      key={card?.key || index}
                    >
                      <MetricsCard
                        className={`dbd-performance-metric-card${
                          isMatchCard ? ' dbd-performance-match-card' : ''
                        }`}
                        style={metricCardStyle}
                        titleCn={metricConfig?.nameCn || card?.name}
                        titleEn={metricConfig?.nameEn || card?.name}
                        subtitleCn={metricConfig?.unitCn || card?.unit}
                        subtitleEn={metricConfig?.unitEn || card?.unit}
                        value={value}
                        formatRule={
                          metricConfig?.formatRule ||
                          (isMatchCard ? 'thousandsInit' : 'twoDecimal')
                        }
                        dodClassName="dbd-performance-metric-rates"
                        dodData={getCardDodData(card, metricConfig)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          }
        />
      </Card.Body>
    </Card>
  );
};

const percentageColumn = (name: string, code: string, width = 130) => ({
  nameEn: name,
  nameCn: name,
  code,
  width,
  align: 'right',
  features: {
    format: 'percent2Decimal',
    downLoadFormat: '0.00%',
    progressBar: {},
    cell: { showProgressBar: true },
  },
});

const renderCountryFlagCell = (value: any, record: any) => (
  <span className="dbd-performance-country-flag-cell">
    {record?.countryFlag ? (
      <img src={record.countryFlag} alt="" />
    ) : null}
    <span>{isEmptyMetricValue(value) ? '-' : value}</span>
  </span>
);

const buildMetricColumn = (metric: any) => ({
  nameEn: metric?.nameEn,
  nameCn: metric?.nameCn,
  code: metric?.metricKey,
  width: metric?.width,
  align: metric?.align || 'right',
  lock: metric?.lock,
  ...(metric?.showCountryFlag ? { render: renderCountryFlagCell } : {}),
  features: {
    ...(metric?.formatRule ? { format: metric.formatRule } : {}),
    ...(metric?.downLoadFormat ? { downLoadFormat: metric.downLoadFormat } : {}),
    ...(metric?.showProgressBar
      ? {
        progressBar: {},
        cell: { showProgressBar: true },
      }
      : {}),
  },
});

const buildTableColumns = (
  componentMetrics: any[],
  fallbackColumns: any[],
  tableGroup?: any,
) => {
  if (!componentMetrics?.length) return fallbackColumns;
  const columns = componentMetrics.map(buildMetricColumn);
  const groupMetricKeys = tableGroup?.metricKeys || [];
  if (!groupMetricKeys.length) return columns;
  const groupMetricKeySet = new Set(groupMetricKeys);
  const children = columns.filter((column: any) =>
    groupMetricKeySet.has(column.code),
  );
  return [
    ...columns.filter((column: any) => !groupMetricKeySet.has(column.code)),
    {
      nameEn: tableGroup?.nameEn,
      nameCn: tableGroup?.nameCn,
      align: tableGroup?.align || 'center',
      children,
    },
  ];
};

const trendTableColumns: any[] = [
  {
    nameEn: '时间',
    nameCn: '时间',
    code: 'time',
    width: 180,
    align: 'left',
    lock: true,
  },
  {
    nameEn: '网络类型',
    nameCn: '网络类型',
    code: 'network',
    width: 110,
    align: 'left',
  },
  percentageColumn('占比(%)', 'share'),
  {
    nameEn: 'Average Latency(ms)',
    nameCn: '平均网络延迟(ms)',
    code: 'latency',
    width: 160,
    align: 'right',
    features: { format: 'twoDecimal', downLoadFormat: '0.00' },
  },
  {
    nameEn: 'Network Status Distribution',
    nameCn: '网络状态分布占比',
    align: 'center',
    children: [
      percentageColumn('0-100(ms) / 占比(%)', 'p0', 155),
      percentageColumn('100-300(ms) / 占比(%)', 'p30', 165),
      percentageColumn('>300(ms) / 占比(%)', 'p60', 175),
    ],
  },
];

export const NetworkLatencyTrend = ({
  className,
  config = {},
  data: propsData,
  dataKey = 'networkLatency',
}: {
  platform: Platform;
  className?: string;
  config?: any;
  data?: any;
  dataKey?: string;
}) => {
  const { formatMessage } = useIntl();
  const { data, loading, isError } = getNetworkLatencyState(propsData, dataKey);
  const componentMetrics = config?.componentMetrics || [];
  const chartMetrics = useMemo(
    () =>
      componentMetrics.filter(
        (metric: any) =>
          metric?.metricKey !== 'games' &&
          metric?.metricKey !== 'matches' &&
          !String(metric?.metricKey || '').includes('_matches'),
      ),
    [componentMetrics],
  );
  const chartHeight = config?.height || 280;
  const tableHeight = config?.tableHeight || 350;
  const boxHeight = Math.max(chartHeight, tableHeight);
  const tableColumns = useMemo(
    () => buildTableColumns(config?.tableMetrics, trendTableColumns),
    [config?.tableMetrics],
  );
  const trend = useMemo(() => {
    const times = data?.netDataTimes || Object.keys(data?.netHoursData || {});
    const rowsByTime = data?.netHoursData || {};
    return {
      xAxisData: times,
      series: chartMetrics.map((metric: any, index: number) => ({
        name: metric?.nameCn,
        type: 'line',
        symbol: 'circle',
        symbolSize: 7,
        showSymbol: false,
        connectNulls: false,
        emphasis: { scale: false },
        yAxisIndex: metric?.yAxisIndex || 0,
        z: index + 2,
        itemStyle: { color: metric?.color },
        lineStyle: { color: metric?.color },
        data: times.map((time: string) => {
          const rows = rowsByTime?.[time] || [];
          const networkName = metric?.metricKey?.split('_')?.[0]?.toUpperCase();
          const row = rows.find((item: any) =>
            metric?.networkType !== undefined
              ? Number(item?.NetworkType) === Number(metric.networkType)
              : NETWORK_TYPE_NAMES[item?.NetworkType] === networkName,
          );
          const nullAsZero = metric?.nullAsZero ?? config?.nullAsZero;
          return resolveChartMetricValue(
            metric?.metricKey?.includes('matches')
              ? row?.MergeCount
              : row?.Value,
            nullAsZero,
          );
        }),
      })),
    };
  }, [chartMetrics, config?.nullAsZero, data]);
  const tableRows = useMemo(
    () =>
      (data?.netDelayTable || [])
        .filter((item: any) => Number(item?.NetworkType) !== 0)
        .map((item: any, index: number) => ({
          ...toTableRow(item),
          time: item?.CalendarTime,
          network:
            NETWORK_TYPE_NAMES[item?.NetworkType] ||
            String(item?.NetworkType),
          code: `${item?.CalendarTime}-${item?.NetworkType}-${index}`,
        })),
    [data],
  );
  const selectedLegendData = useMemo(() => {
    const unselectedKeys = config?.unselectedLegendKeys;
    if (!Array.isArray(unselectedKeys) || !unselectedKeys.length) {
      return {};
    }
    const unselectedSet = new Set(unselectedKeys);
    return chartMetrics.reduce((result: Record<string, boolean>, metric: any) => {
      const isUnselected =
        unselectedSet.has(metric?.metricKey) ||
        unselectedSet.has(metric?.nameCn) ||
        unselectedSet.has(metric?.nameEn);
      if (isUnselected && metric?.nameCn) {
        result[metric.nameCn] = false;
      }
      return result;
    }, {});
  }, [chartMetrics, config?.unselectedLegendKeys]);
  const formatRuleByName = useMemo(
    () =>
      chartMetrics.reduce((result: Record<string, string>, metric: any) => {
        if (metric?.nameCn) {
          result[metric.nameCn] = metric?.formatRule || 'twoDecimal';
        }
        return result;
      }, {}),
    [chartMetrics],
  );
  const trendYAxis = (config?.yAxis || []).filter(
    (_axis: any, index: number) => index === 0 || chartMetrics.some(
      (metric: any) => Number(metric?.yAxisIndex) === index,
    ),
  );
  const tooltipCacheRef = useRef<{ key: string; html: string }>({
    key: '',
    html: '',
  });
  const trendChart = (
    <LineChart
      height={chartHeight}
      xAxisData={trend.xAxisData}
      legendData={trend.series.map((item: any) => item.name)}
      selectedLegendData={selectedLegendData}
      formatterFunc={(params: any[] = []) => {
        const axisValue = params?.[0]?.axisValue || '';
        const cacheKey = params
          .map((item: any) => `${item?.seriesName}:${item?.value}`)
          .join('|');
        const tooltipKey = `${axisValue}::${cacheKey}`;
        if (tooltipCacheRef.current.key === tooltipKey) {
          return tooltipCacheRef.current.html;
        }
        const html = getEchartTooltipText({
          title: axisValue,
          data: params.map((item: any) => ({
            name: item?.seriesName,
            metricsNum: formatMetric(
              item?.value,
              formatRuleByName[item?.seriesName] || 'twoDecimal',
            ),
            color: item?.color,
          })),
        });
        tooltipCacheRef.current = { key: tooltipKey, html };
        return html;
      }}
      series={trend.series}
      colorArr={chartMetrics.map((item: any) => item?.color)}
      setOtherConfigFunc={(option: any) => ({
        ...option,
        tooltip: {
          ...(option.tooltip || {}),
          transitionDuration: 0,
          hideDelay: 0,
          enterable: false,
          axisPointer: {
            ...(option.tooltip?.axisPointer || {}),
            type: 'line',
            snap: true,
            animation: false,
          },
        },
        grid: {
          ...(option.grid || {}),
          left: 28,
          right: 16,
          top: 36,
          bottom: 56,
          containLabel: true,
        },
        graphic: [
          {
            type: 'text',
            left: 0,
            top: 4,
            z: 100,
            style: {
              text: trendYAxis?.[0]?.nameCn || '网络延迟(ms)',
              fill: '#838697',
              fontSize: 12,
              lineHeight: 16,
              textAlign: 'left',
            },
          },
          ...(trendYAxis?.[1]
            ? [
                {
                  type: 'text',
                  right: 0,
                  top: 4,
                  z: 100,
                  style: {
                    text: trendYAxis[1]?.nameCn || '',
                    fill: '#838697',
                    fontSize: 12,
                    lineHeight: 16,
                    textAlign: 'right',
                  },
                },
              ]
            : []),
        ],
        legend: Array.isArray(option.legend)
          ? option.legend
          : {
            ...(option.legend || {}),
            bottom: 0,
          },
        xAxis: (Array.isArray(option.xAxis) ? option.xAxis : [option.xAxis]).map(
          (axis: any) => ({
            ...axis,
            axisPointer: {
              ...(axis?.axisPointer || {}),
              type: 'line',
              snap: true,
              animation: false,
            },
            axisLabel: {
              ...(axis?.axisLabel || {}),
              hideOverlap: true,
              margin: 12,
              lineHeight: 16,
              formatter: formatTrendXAxisLabel,
            },
          }),
        ),
        yAxis: trendYAxis.map((axis: any, index: number) => ({
          type: 'value',
          // 左右侧单位改用 graphic，分别与「数据时间」「下载」对齐
          name: '',
          min: 0,
          position: index === 0 ? 'left' : 'right',
          alignTicks: true,
          splitLine:
            index === 0
              ? { show: true, lineStyle: { type: 'dashed', color: '#E4E7EF' } }
              : { show: false },
          axisLabel: {
            color: '#838697',
            fontSize: 12,
            margin: index === 0 ? 12 : 8,
            formatter: (value: number) =>
              axis?.formatRule === 'thousandsInit'
                ? yAxisFormat(value)
                : formatMetric(value, axis?.formatRule || 'twoDecimal'),
          },
        })),
      })}
    />
  );
  const trendTable = (
    <CommonTable
      className="dbd-performance-embedded-table"
      data={{}}
      metricsCardWrapProps={{ titleEn: '', titleCn: '' }}
      commonBaseTableProps={{
        dataSource: tableRows,
        columns: tableColumns,
        height: tableHeight,
        tableConfig: {
          primaryKey: 'code',
          isOpenForegroundPage: true,
          pagination: {
            pageSize: config?.pageSize || 5,
            pageIndex: 1,
          },
        },
        featConfig: { sortable: true },
        isLoading: false,
      }}
    />
  );

  return (
    <CardBox
      className={`dbd-performance-card dbd-performance-network-card dbd-performance-trend-card ${className || ''}`.trim()}
      bordered={false}
      titleCom={config?.titleCn || '网络延迟趋势'}
      filterSlot={
        !loading && !isError && (trend?.xAxisData || []).length ? (
          <FilterSummary data={data} />
        ) : null
      }
      extraOperationAtEnd
      extraOperationSlot={
        <DownloadButton
          tableColumns={tableColumns}
          tableData={tableRows}
          tableTitle={config?.titleCn || '网络延迟趋势'}
          config={{
            fileName: config?.downloadFileName || '网络延迟趋势',
            flattenGroupColumns: true,
          }}
          disabled={loading || isError || !tableRows.length}
        />
      }
      height={boxHeight}
      isLoading={!!loading}
      isError={isError}
      isNodata={
        !loading && !isError && !(trend?.xAxisData || []).length
      }
      renderList={[
        {
          type: 'lineChart',
          svgType: 'discount-chart',
          content: formatMessage({ id: 'common_line_chart' }),
          component: trendChart,
        },
        {
          type: 'sheet',
          svgType: 'sheet',
          content: formatMessage({ id: 'common_table' }),
          component: trendTable,
        },
      ]}
    />
  );
};

const networkColumns: any[] = [
  {
    nameEn: '网络类型',
    nameCn: '网络类型',
    code: 'network',
    width: 110,
    lock: true,
  },
  percentageColumn('占比(%)', 'share'),
  {
    nameEn: '平均网络延时(ms)',
    nameCn: '平均网络延时(ms)',
    code: 'latency',
    width: 160,
    align: 'right',
    features: { format: 'twoDecimal', downLoadFormat: '0.00' },
  },
  percentageColumn('0-100(ms) 占比(%)', 'p0', 155),
  percentageColumn('100-300(ms) 占比(%)', 'p30', 165),
  percentageColumn('>300(ms) 占比(%)', 'p60', 175),
];

export const NetworkLatencyDistribution = ({
  config = {},
  data: propsData,
  dataKey = 'networkLatency',
}: {
  platform: Platform;
  config?: any;
  data?: any;
  dataKey?: string;
}) => {
  const { data, loading, isError } = getNetworkLatencyState(propsData, dataKey);
  const columns = useMemo(
    () =>
      applyDistRangeLabels(
        buildTableColumns(
          config?.componentMetrics,
          networkColumns,
          config?.tableGroup,
        ),
        config?.mapRanges,
      ),
    [config?.componentMetrics, config?.mapRanges, config?.tableGroup],
  );
  const rows = useMemo(
    () =>
      (data?.networkAgg || [])
        .filter((item: any) => Number(item?.NetworkType) !== 0)
        .map((item: any) => ({
          ...toTableRow(item),
          network: NETWORK_TYPE_NAMES[item?.NetworkType] || String(item?.NetworkType),
        })),
    [data],
  );
  const pieMetricKey = config?.pieMetricKey || 'share';
  // 公共饼图会过滤 0 值；仅当前页面用极小占位值保留 0% 标签。
  const pieData = useMemo(() => {
    const total = rows.reduce(
      (sum, item) => sum + Number(item?.[pieMetricKey] || 0),
      0,
    );
    return rows.map((item) => {
      const rawValue = Number(item?.[pieMetricKey] || 0);
      return {
        name: item.network,
        value: rawValue > 0 ? rawValue : Number.EPSILON,
        rawValue,
        rawTotal: total,
      };
    });
  }, [pieMetricKey, rows]);
  const piePercentRule =
    config?.chartPercentDecimal === 2
      ? 'twoDecimal'
      : config?.chartPercentDecimal === 1
        ? 'oneDecimal'
        : 'int';
  return (
    <Card className="dbd-performance-card dbd-performance-network-card">
      <Card.Body>
        <SectionTitle
          title={config?.titleCn || '网络延迟分布'}
          info={getConfigTitleInfo(
            config,
            '数据为所选时间段内累计数据的分布',
            'The data shows the cumulative distribution within the selected time range.',
          )}
          operation={
            <DownloadButton
              tableColumns={columns}
              tableData={rows}
              tableTitle={config?.titleCn || '网络延迟分布'}
              config={{
                fileName: config?.downloadFileName || '网络延迟分布',
                flattenGroupColumns: true,
              }}
              disabled={loading || isError || !rows.length}
            />
          }
        />
        {!loading &&
        !isError &&
        rows.some((item) => item?.[config?.pieMetricKey || 'share']) ? (
          <FilterSummary data={data} />
        ) : null}
        <Abnormal
          style={{ height: config?.chartHeight || 310 }}
          isLoading={!!loading}
          isError={isError}
          isNoData={
            !loading &&
            !isError &&
            !rows.some((item) => item?.[config?.pieMetricKey || 'share'])
          }
          component={
            <div className="dbd-performance-distribution-layout">
              <div className="dbd-performance-pie-wrap">
                <PieChart
                  height={config?.chartHeight || 310}
                  chartData={pieData}
                  legendData={rows.map((item) => item.network)}
                  colorArr={config?.colors || NETWORK_COLORS}
                  isShowLegend={false}
                  formatterFunc={(params: any) =>
                    getEchartTooltipText({
                      data: [
                        {
                          name: params?.name,
                          metricsNum: `${formatMetric(
                            params?.data?.rawValue === 0 ? 0 : params?.percent,
                            piePercentRule,
                          )}%`,
                          color: params?.color,
                        },
                      ],
                    })
                  }
                  isNodata={false}
                  config={{
                    chartPercentDecimal: config?.chartPercentDecimal ?? 2,
                  }}
                  setOtherConfigFunc={(option: any) => ({
                    ...option,
                    series: option.series.map((series: any) => ({
                      ...series,
                      minAngle: 0,
                      radius: '58%',
                      center: ['50%', '50%'],
                      label: {
                        ...(series?.label || {}),
                        formatter: ({ name, data: item }: any) => {
                          const percent = item?.rawTotal
                            ? (Number(item?.rawValue || 0) / item.rawTotal) *
                              100
                            : 0;
                          return `{name|${name}}：{per|${formatMetric(
                            percent,
                            piePercentRule,
                          )}%}\n{hr|}\n{a|}`;
                        },
                      },
                    })),
                  })}
                />
              </div>
              <div className="dbd-performance-table-wrap">
                <CommonTable
                  className="dbd-performance-embedded-table"
                  data={{}}
                  metricsCardWrapProps={{ titleEn: '', titleCn: '' }}
                  commonBaseTableProps={{
                    dataSource: rows,
                    columns,
                    height: config?.tableHeight || 310,
                    tableConfig: {
                      primaryKey: 'network',
                      isShowPagination: false,
                    },
                    featConfig: { sortable: true },
                    isLoading: false,
                  }}
                />
              </div>
            </div>
          }
        />
      </Card.Body>
    </Card>
  );
};

const isCountryLevelZoneRow = (item: any): boolean =>
  !item?.CityName && !item?.ProvinceName;

const isAllCountryZoneRow = (item: any): boolean =>
  item?.CountryName === '全部' || item?.CountryNameEn === 'All';

const unwrapCountryValue = (value: any): any => {
  if (value === undefined || value === null || value === '') {
    return value;
  }
  if (Array.isArray(value)) {
    return unwrapCountryValue(value[0]);
  }
  if (typeof value === 'object') {
    if (value.country_code !== undefined && value.country_code !== null) {
      return value.country_code;
    }
    if (value.value !== undefined && value.value !== null) {
      return value.value;
    }
  }
  return value;
};

const getCountryFilterNames = (country?: any): string[] => {
  const names = [country?.country_name_ch, country?.country_name_en]
    .map((name) => String(name || '').trim())
    .filter(Boolean);
  const flagMatch = String(country?.countryFlag || '').match(
    /\/flags\/([^/.]+)\./i,
  );
  if (flagMatch?.[1]) {
    names.push(flagMatch[1].replace(/_/g, ' '));
  }
  return Array.from(new Set(names));
};

const isCountryNamedZoneRow = (
  item: any,
  countryNames: string[],
): boolean => {
  const province = String(item?.ProvinceName || '').trim();
  const city = String(item?.CityName || '').trim();
  return !city && countryNames.includes(province);
};

/** 选中国家后只保留该国省份/城市，去掉国家级汇总和「印度」这种伪省份 */
const pickSelectedCountryRows = (
  source: any[] = [],
  selectedCountry?: any,
): any[] => {
  const countryNames = getCountryFilterNames(selectedCountry);
  const matched = source.filter((item: any) => {
    if (!countryNames.length) {
      return false;
    }
    const itemNames = [item?.CountryName, item?.CountryNameEn]
      .map((name) => String(name || '').trim())
      .filter(Boolean);
    return itemNames.some((name) => countryNames.includes(name));
  });
  const scoped = countryNames.length ? matched : source;
  return scoped.filter((item: any) => {
    if (isAllCountryZoneRow(item)) {
      return false;
    }
    if (isCountryLevelZoneRow(item)) {
      return false;
    }
    if (isCountryNamedZoneRow(item, countryNames)) {
      return false;
    }
    return true;
  });
};

/** 全部国家视图：优先用国家级汇总；某国只有城市行时回填一行，避免地图缺国家 */
const pickWorldCountryRows = (source: any[] = []): any[] => {
  const countryLevel: any[] = [];
  const cityFallback: Record<string, any> = {};
  source.forEach((item: any) => {
    if (isAllCountryZoneRow(item)) {
      return;
    }
    const countryKey = item?.CountryNameEn || item?.CountryName;
    if (!countryKey) {
      return;
    }
    if (isCountryLevelZoneRow(item)) {
      countryLevel.push(item);
      return;
    }
    const current = cityFallback[countryKey];
    if (!current || (item?.CountRatio || 0) > (current?.CountRatio || 0)) {
      cityFallback[countryKey] = item;
    }
  });
  const countryKeys = new Set(
    countryLevel.map(
      (item: any) => item?.CountryNameEn || item?.CountryName,
    ),
  );
  const extras = Object.keys(cityFallback)
    .filter((key) => !countryKeys.has(key))
    .map((key) => cityFallback[key]);
  return countryLevel.concat(extras);
};

const countryColumns: any[] = [
  {
    nameEn: '国家/地区',
    nameCn: '国家/地区',
    code: 'country',
    width: 150,
    lock: true,
  },
  { nameEn: '网络类型', nameCn: '网络类型', code: 'network', width: 100 },
  percentageColumn('占比(%)', 'share'),
  {
    nameEn: '平均网络延时(ms)',
    nameCn: '平均网络延时(ms)',
    code: 'latency',
    width: 160,
    align: 'right',
    features: { format: 'twoDecimal', downLoadFormat: '0.00' },
  },
  percentageColumn('0-100(ms) 占比(%)', 'p0', 155),
  percentageColumn('100-300(ms) 占比(%)', 'p30', 165),
  percentageColumn('>300(ms) 占比(%)', 'p60', 175),
];

export const CountryLatencyDistribution = ({
  config = {},
  countryRegionOptions = [],
  data: propsData,
  dataKey = 'networkLatency',
}: {
  platform: Platform;
  config?: any;
  countryRegionOptions?: any[];
  data?: any;
  dataKey?: string;
}) => {
  const { filterVal } = useContext(FilterContext) || ({} as any);
  const contextCountryRegionOptions = useContext(CountryRegionOptionsContext);
  const resolvedCountryRegionOptions =
    (Array.isArray(countryRegionOptions) && countryRegionOptions.length
      ? countryRegionOptions
      : contextCountryRegionOptions) || [];
  const { data, loading, isError } = getNetworkLatencyState(propsData, dataKey);
  const [network, setNetwork] = useState('all');
  // null = 全选；Set 内为选中的 colorIndex，用于图例点击联动地图
  const [activeLegendIndexes, setActiveLegendIndexes] = useState<Set<number> | null>(
    null,
  );
  const countryFlagByName = useMemo(() => {
    const result = new Map<string, string>();
    resolvedCountryRegionOptions.forEach((item: any) => {
      if (!item?.countryFlag) return;
      [item?.country_name_ch, item?.country_name_en]
        .filter(Boolean)
        .forEach((name) => result.set(String(name), item.countryFlag));
    });
    return result;
  }, [resolvedCountryRegionOptions]);
  const selectedCountry = useMemo(() => {
    const countryValue = unwrapCountryValue(
      filterVal?.country ?? propsData?.filter?.country,
    );
    if (
      countryValue === undefined ||
      countryValue === null ||
      countryValue === '' ||
      Number(countryValue) === -1
    ) {
      return undefined;
    }
    return resolvedCountryRegionOptions.find(
      (item: any) => Number(item?.country_code) === Number(countryValue),
    );
  }, [
    filterVal?.country,
    propsData?.filter?.country,
    resolvedCountryRegionOptions,
  ]);
  const selectedCountryNameCh = String(selectedCountry?.country_name_ch || '');
  const selectedCountryNameEn = String(selectedCountry?.country_name_en || '');
  const isRegionSelected = isRegionCountryName(
    selectedCountryNameCh || selectedCountryNameEn,
  );
  const showCountryMap =
    Boolean(selectedCountry) &&
    !isRegionSelected &&
    isGeoCountryOption(selectedCountry);
  const countryIso2 = showCountryMap ? getCountryIso2(selectedCountry) : '';
  const selectedCountryText = (() => {
    if (!selectedCountry || Number(selectedCountry?.country_code) === -1) {
      return '';
    }
    if (getLocale() === 'zh-CN') {
      return selectedCountryNameCh || selectedCountryNameEn;
    }
    return selectedCountryNameEn || selectedCountryNameCh;
  })();
  const [countryMapJson, setCountryMapJson] = useState<any>(null);
  useEffect(() => {
    if (!showCountryMap || !countryIso2) {
      setCountryMapJson(null);
      return;
    }
    let cancelled = false;
    loadCountryMapJson(countryIso2)
      .then((json) => {
        if (!cancelled) {
          setCountryMapJson(json);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCountryMapJson(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [countryIso2, showCountryMap]);
  const useCountryGeo = Boolean(showCountryMap && countryMapJson);
  const countryFeatureNameIndex = useMemo(
    () => (useCountryGeo ? buildFeatureNameIndex(countryMapJson) : undefined),
    [countryMapJson, useCountryGeo],
  );
  const countryMapCenter = showCountryMap
    ? getCountryMapCenter(selectedCountryNameCh, selectedCountryNameEn)
    : undefined;
  const countryMapZoom =
    showCountryMap && countryMapCenter && !useCountryGeo
      ? getCountryMapZoom(selectedCountryNameCh)
      : undefined;
  const networkOptions = config?.networkOptions || NETWORK_OPTIONS;
  const mapColors = config?.mapColors || MAP_COLORS;
  const rows = useMemo(
    () => {
      const source = flattenZoneAgg(
        data?.[
          network === 'all'
            ? 'zoneAggAll'
            : `zoneAgg${network === 'wifi' ? 'Wifi' : network.toUpperCase()}`
        ],
      );
      const matchedSource = selectedCountry
        ? pickSelectedCountryRows(source, selectedCountry)
        : pickWorldCountryRows(source);
      return matchedSource.map((item: any) => ({
        ...toTableRow(item),
        code: [
          item?.CountryNameEn || item?.CountryName,
          item?.ProvinceName,
          item?.CityName,
        ]
          .filter(Boolean)
          .join('-'),
        country: selectedCountry
          ? item?.CityName || item?.ProvinceName || '全部'
          : item?.CountryName || item?.CountryNameEn,
        countryFlag:
          countryFlagByName.get(item?.CountryName) ||
          countryFlagByName.get(item?.CountryNameEn) ||
          '',
        province: item?.ProvinceName,
        city: item?.CityName,
        mapName: getMapSeriesName(item),
        network: network === 'all' ? '全部' : network.toUpperCase(),
      }));
    },
    [
      countryFlagByName,
      data,
      network,
      selectedCountry,
    ],
  );
  const mapData = useMemo(() => {
    const byName: Record<string, { name: string; value: number; share: number; }> = {};
    rows.forEach((item) => {
      const name = useCountryGeo
        ? toMapRegionName(item.province, countryFeatureNameIndex)
        : item.mapName;
      if (!name) return;
      if (!useCountryGeo && !isMappedCountryName(item.mapName)) return;
      const value = item?.[config?.mapMetric?.metricKey || 'latency'];
      if (value === null || value === undefined) return;
      const current = byName[name];
      if (!current || (item.share || 0) > current.share) {
        byName[name] = {
          name,
          value,
          share: item.share || 0,
        };
      }
    });
    return Object.values(byName).map(({ name, value }) => ({
      name,
      value,
    }));
  }, [
    config?.mapMetric?.metricKey,
    countryFeatureNameIndex,
    rows,
    useCountryGeo,
  ]);
  const mapRanges = useMemo(() => {
    const minMs = Number(config?.mapLegendMinMs);
    const maxMs = Number(config?.mapLegendMaxMs);
    const midMs = Number(config?.mapLegendMidMs);
    if (
      Number.isFinite(minMs) &&
      Number.isFinite(maxMs) &&
      maxMs > minMs
    ) {
      const middle =
        Number.isFinite(midMs) && midMs > minMs && midMs < maxMs
          ? midMs
          : Math.round((minMs + maxMs) / 2);
      return [minMs, middle, maxMs];
    }
    if (Array.isArray(config?.mapRanges) && config.mapRanges.length) {
      return config.mapRanges;
    }
    const maxValue = Math.max(
      0,
      ...mapData.map((item) => Number(item.value) || 0),
    );
    return buildMapRangesFromMax(maxValue);
  }, [
    config?.mapLegendMaxMs,
    config?.mapLegendMidMs,
    config?.mapLegendMinMs,
    config?.mapRanges,
    mapData,
  ]);
  const mapLegendUnit = config?.mapLegendUnit ?? 'ms';
  const mapLegend = useMemo(() => {
    if (Array.isArray(config?.mapLegend) && config.mapLegend.length) {
      return [...config.mapLegend].sort(
        (a: any, b: any) => (b?.colorIndex ?? 0) - (a?.colorIndex ?? 0),
      );
    }
    return buildMapLegendFromRanges(mapRanges, mapLegendUnit);
  }, [config?.mapLegend, mapLegendUnit, mapRanges]);
  const mapLegendScale = useMemo(() => {
    const min =
      config?.mapLegendScale?.min ?? mapRanges[0] ?? config?.mapLegendMinMs ?? 0;
    const max =
      config?.mapLegendScale?.max ??
      mapRanges[mapRanges.length - 1] ??
      config?.mapLegendMaxMs ??
      200;
    return { min, max };
  }, [config?.mapLegendMaxMs, config?.mapLegendMinMs, config?.mapLegendScale, mapRanges]);
  const mapPieces = buildMapPieces(mapRanges, mapColors);
  const displayMapData = useMemo(() => {
    if (!activeLegendIndexes) {
      return mapData;
    }
    return mapData.map((item) => {
      const pieceIndex = getMapPieceIndex(Number(item?.value), mapRanges);
      if (pieceIndex < 0 || !activeLegendIndexes.has(pieceIndex)) {
        return { ...item, value: null };
      }
      return item;
    });
  }, [activeLegendIndexes, mapData, mapRanges]);
  const handleLegendClick = (colorIndex: number): void => {
    const allIndexes = mapLegend
      .map((item: any) => item?.colorIndex)
      .filter((index: number) => typeof index === 'number');
    setActiveLegendIndexes((prev) => {
      const current = prev ?? new Set(allIndexes);
      const next = new Set(current);
      if (next.has(colorIndex)) {
        next.delete(colorIndex);
      } else {
        next.add(colorIndex);
      }
      if (!next.size || next.size === allIndexes.length) {
        return null;
      }
      return next;
    });
  };
  const handleNetworkChange = (value: string): void => {
    setNetwork(value);
    setActiveLegendIndexes(null);
  };
  const columns = useMemo(
    () => {
      const sourceColumns = selectedCountry
        ? countryColumns.map((column) =>
            column.code === 'country'
              ? { ...column, nameEn: 'Province/Region', nameCn: '省份/地区' }
              : column,
          )
        : countryColumns;
      return applyDistRangeLabels(
        buildTableColumns(
          config?.componentMetrics,
          sourceColumns,
          config?.tableGroup,
        ),
        mapRanges,
      );
    },
    [config?.componentMetrics, config?.tableGroup, mapRanges, selectedCountry],
  );
  return (
    <Card className="dbd-performance-card dbd-performance-network-card">
      <Card.Body>
        <SectionTitle
          title={config?.titleCn || '国家/地区网络延迟分布'}
          info={getConfigTitleInfo(
            config,
            '数据为所选时间段内累计数据的分布，国家/地区信息来自IP解析。',
            'The data shows the cumulative distribution within the selected time range. Country/region information is parsed from IP.',
          )}
          operation={
            <DownloadButton
              tableColumns={columns}
              tableData={rows}
              tableTitle={config?.titleCn || '国家/地区网络延迟分布'}
              config={{
                fileName:
                  config?.downloadFileName || '国家地区网络延迟分布',
                flattenGroupColumns: true,
              }}
              disabled={loading || isError || !rows.length}
            />
          }
        />
        {!loading && !isError && rows.length ? (
          <FilterSummary
            data={data}
            countryText={selectedCountryText}
          />
        ) : null}
        <Segment
          options={networkOptions}
          value={network}
          onChange={handleNetworkChange}
        />
        <Abnormal
          style={{ minHeight: (config?.mapHeight || 350) + (config?.tableHeight || 350) }}
          isLoading={!!loading}
          isError={isError}
          isNoData={!loading && !isError && !rows.length}
          component={
            <>
              <div className="dbd-performance-map-wrap">
                <WorldMapEchart
                  key={`${useCountryGeo ? countryIso2 : 'world'}-${
                    selectedCountry?.country_code ?? -1
                  }`}
                  height={config?.mapHeight || 350}
                  data={displayMapData}
                  mapColor={mapColors}
                  mapColorV={mapRanges}
                  mapName={useCountryGeo ? `country-${countryIso2}` : 'world'}
                  mapJson={useCountryGeo ? countryMapJson : undefined}
                  mapCenter={useCountryGeo ? undefined : countryMapCenter}
                  mapZoom={countryMapZoom}
                  setOtherConfigFunc={(option: any) => ({
                    ...option,
                    visualMap: [
                      {
                        show: false,
                        type: 'piecewise',
                        seriesIndex: 0,
                        pieces: mapPieces,
                        outOfRange: {
                          color: '#f2f2f2',
                        },
                      },
                    ],
                  })}
                  formatterFunc={(params: any) => {
                    const { name, value, color, data: regionData } = params || {};
                    const rawValue =
                      regionData &&
                      Object.prototype.hasOwnProperty.call(regionData, 'value')
                        ? regionData.value
                        : value;
                    const metricValue = toMetricNumber(rawValue);
                    const metricText =
                      regionData == null || metricValue === null
                        ? '-'
                        : `${formatMetric(
                            metricValue,
                            config?.mapMetric?.formatRule || 'twoDecimal',
                          )}${config?.mapMetric?.unitCn || 'ms'}`;
                    return getEchartTooltipText({
                      hasDot: false,
                      data: [
                        {
                          name,
                          metricsNum: metricText,
                          color,
                        },
                      ],
                    });
                  }}
                />
                <div className="dbd-performance-map-legend">
                  <span className="dbd-performance-map-legend__scale dbd-performance-map-legend__scale--max">
                    {mapLegendScale.max}
                    {mapLegendUnit}
                  </span>
                  {mapLegend.map((item: any) => {
                    const isActive =
                      !activeLegendIndexes ||
                      activeLegendIndexes.has(item?.colorIndex);
                    return (
                      <span
                        key={item?.label}
                        className={`dbd-performance-map-legend__item${
                          isActive ? '' : ' dbd-performance-map-legend__item--off'
                        }`}
                        onClick={() => handleLegendClick(item?.colorIndex)}
                      >
                        <i
                          style={{ background: mapColors[item?.colorIndex] }}
                        />
                        {item?.label}
                      </span>
                    );
                  })}
                  <span className="dbd-performance-map-legend__scale dbd-performance-map-legend__scale--min">
                    {mapLegendScale.min}
                    {mapLegendUnit}
                  </span>
                </div>
              </div>
              <CommonTable
                data={{}}
                metricsCardWrapProps={{ titleEn: '', titleCn: '' }}
                commonBaseTableProps={{
                  dataSource: rows,
                  columns,
                  height: config?.tableHeight || 350,
                  tableConfig: {
                    primaryKey: 'code',
                    isOpenForegroundPage: true,
                    pagination: {
                      pageSize: config?.pageSize || 5,
                      pageIndex: 1,
                    },
                  },
                  featConfig: { sortable: true },
                  isLoading: false,
                }}
              />
            </>
          }
        />
      </Card.Body>
    </Card>
  );
};
